import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/header/Header';
import { Footer } from '../components/footer/Footer';
import { AppDownloadModal } from '../components/common/AppDownloadModal';
import { RECIPES_DATA } from '../data/recipesData';
import { FaClock, FaStar, FaUtensils, FaSearch, FaArrowLeft, FaDownload } from 'react-icons/fa';
import { SEOHead } from '../components/common/SEOHead';
import './RecipesPage.css';

export const RecipesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract ?q= from query params
  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get('q') || '';

  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [selectedDiet, setSelectedDiet] = useState('All');
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [activeRecipeTitle, setActiveRecipeTitle] = useState('');

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q !== null) setSearchQuery(q);
  }, [location.search]);

  const diets = ['All', 'Keto / Low-Carb', 'High Protein', 'Vegetarian', 'Vegan / Healthy', 'Gluten-Free / Keto', 'Dessert'];

  const filteredRecipes = RECIPES_DATA.filter((recipe) => {
    const matchesSearch = recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          recipe.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          recipe.cuisine.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDiet = selectedDiet === 'All' || recipe.diet === selectedDiet;
    return matchesSearch && matchesDiet;
  });

  const handleSeeRecipe = (recipeTitle) => {
    setActiveRecipeTitle(`Get ingredients & instructions for "${recipeTitle}"`);
    setIsAppModalOpen(true);
  };

  return (
    <div className="recipes-page-container">
      <SEOHead
        title="Healthy Recipes & Meal Planning Guides — ShareRecipe"
        description="Browse chef-curated healthy recipes with step-by-step preparation guides, nutritional breakdowns, and ingredient lists on ShareRecipe."
        canonicalUrl="https://sharecipe.app/recipes"
      />
      <Header />

      <main className="recipes-main">
        <div className="recipes-hero">
          <button className="back-home-btn" onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </button>
          <h1>Explore 10 Public Healthy Recipes</h1>
          <p>Browse chef-curated healthy recipes with ingredients, preparation guides, and nutritional breakdowns available publicly without login.</p>

          <div className="recipes-search-bar">
            <FaSearch className="search-icon" />
            <input 
              type="text" 
              placeholder="Search recipes by title, ingredients, or cuisine..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="recipes-tags-filter">
            {diets.map((diet) => (
              <button
                key={diet}
                className={`diet-btn ${selectedDiet === diet ? 'active' : ''}`}
                onClick={() => setSelectedDiet(diet)}
              >
                {diet}
              </button>
            ))}
          </div>
        </div>

        <div className="recipes-grid">
          {filteredRecipes.map((recipe) => (
            <div key={recipe.id} className="recipe-card">
              <div className="recipe-card-image">
                <img src={recipe.image} alt={recipe.title} />
                <span className="recipe-diet-badge">{recipe.diet}</span>
              </div>
              <div className="recipe-card-content">
                <div className="recipe-meta-top">
                  <span className="recipe-rating">
                    <FaStar className="star-icon" /> {recipe.rating}
                  </span>
                  <span className="recipe-prep-time">
                    <FaClock /> {recipe.prepTime}
                  </span>
                </div>

                <h3>{recipe.title}</h3>
                <p className="recipe-cuisine"><FaUtensils /> {recipe.cuisine} • {recipe.servings} Servings</p>
                <p className="recipe-desc">{recipe.description}</p>

                <div className="nutrition-pills">
                  <span>{recipe.nutrition.calories}</span>
                  <span>Protein: {recipe.nutrition.protein}</span>
                  <span>Carbs: {recipe.nutrition.carbs}</span>
                </div>

                <div className="recipe-card-actions">
                  <button 
                    className="see-recipe-btn"
                    onClick={() => handleSeeRecipe(recipe.title)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                  >
                    <FaDownload /> See Full Recipe on App
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      <Footer />

      <AppDownloadModal 
        isOpen={isAppModalOpen}
        onClose={() => setIsAppModalOpen(false)}
        title={activeRecipeTitle || "Get the ShareRecipe App"}
      />
    </div>
  );
};
